import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  Upload, Loader2, FileText, Image, FileSpreadsheet, Download,
  Trash2, File, ChevronDown, ChevronUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StaffUploadCenter() {
  const { user, token } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [description, setDescription] = useState("");
  const [expandedUpload, setExpandedUpload] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const isDirector = user?.isOwner || user?.isPrimaryOwner || user?.isAdmin;

  useEffect(() => {
    if (token) fetchUploads();
  }, [token]);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/staff-uploads`, { headers: authHeaders });
      setUploads(res.data.uploads || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target?.files || e.dataTransfer?.files || []);
    if (files.length === 0) return;
    if (!description.trim()) { toast.error("Please enter a description"); return; }

    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);
        formData.append('description', files.length > 1 ? `${description} (${i + 1})` : description);
        await axios.post(`${API}/staff-uploads/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', ...authHeaders }
        });
      }
      toast.success(files.length > 1 ? `${files.length} files uploaded!` : "File uploaded!");
      setDescription("");
      fetchUploads();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm("Delete this upload?")) return;
    try {
      await axios.delete(`${API}/staff-uploads/${uploadId}`, { headers: authHeaders });
      toast.success("Deleted");
      fetchUploads();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return <FileText className="w-4 h-4 text-red-500" />;
    if (fileType === 'xls' || fileType === 'xlsx') return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
    if (['png','jpg','jpeg'].includes(fileType)) return <Image className="w-4 h-4 text-blue-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const renderFilePreview = (upload) => {
    const fileUrl = `${API}/staff-uploads/${upload.id}/file?token=${encodeURIComponent(token)}`;

    if (upload.file_type === 'pdf') {
      return <iframe src={fileUrl} className="w-full rounded-lg border" style={{ height: "400px" }} title={upload.description} />;
    }
    if (['png','jpg','jpeg'].includes(upload.file_type)) {
      return <img src={fileUrl} alt={upload.description} className="max-w-full rounded-lg border" style={{ maxHeight: "400px" }} />;
    }
    if (upload.file_type === 'xls' || upload.file_type === 'xlsx') {
      return <UploadExcelView upload={upload} fileUrl={fileUrl} token={token} />;
    }
    return (
      <a href={fileUrl} download={upload.filename} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
        <Download className="w-4 h-4" /> Download {upload.filename}
      </a>
    );
  };

  return (
    <div data-testid="staff-upload-center" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <Upload className="w-5 h-5 text-blue-600" />
          Staff Upload Center
        </h3>
      </div>

      {/* Upload area */}
      <div
        className={`border-2 border-dashed rounded-xl p-4 transition-colors ${
          dragging ? 'border-blue-500 bg-blue-100' : 'border-blue-200 bg-blue-50'
        }`}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); if (!description.trim()) { toast.error("Enter a description first"); return; } handleUpload(e); }}
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Any requested miscellaneous documentations or files"
            className="flex-1 border rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="upload-description-input"
          />
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleUpload}
              disabled={uploading || !description.trim()}
              className="hidden"
              id="staff-upload-file"
            />
            <Button
              onClick={() => {
                if (!description.trim()) { toast.error("Enter a description first"); return; }
                fileInputRef.current?.click();
              }}
              disabled={uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              data-testid="upload-file-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload File
            </Button>
          </div>
        </div>
      </div>

      {/* Uploads list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
      ) : uploads.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">No uploads yet</div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          {uploads.map(upload => (
            <div key={upload.id} className="border-b last:border-0 border-gray-100">
              <div
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedUpload(expandedUpload === upload.id ? null : upload.id)}
                data-testid={`upload-item-${upload.id}`}
              >
                <div className="flex items-center gap-3">
                  {getFileIcon(upload.file_type)}
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{upload.description}</p>
                    <p className="text-xs text-gray-400">
                      {isDirector && <span className="font-medium text-gray-500">{upload.staffName} &middot; </span>}
                      {upload.filename} &middot; {new Date(upload.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <a
                    href={`${API}/staff-uploads/${upload.id}/file?token=${encodeURIComponent(token)}`}
                    download={upload.filename}
                    onClick={e => e.stopPropagation()}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <Button
                    variant="ghost" size="sm"
                    className="text-red-400 hover:text-red-600 h-7 px-2"
                    onClick={e => { e.stopPropagation(); handleDelete(upload.id); }}
                    data-testid={`delete-upload-${upload.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedUpload === upload.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>
              {expandedUpload === upload.id && (
                <div className="px-4 pb-4 bg-gray-50/50">
                  {renderFilePreview(upload)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Excel viewer for staff uploads
function UploadExcelView({ upload, fileUrl, token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/staff-uploads/${upload.id}/excel-data?token=${encodeURIComponent(token)}`)
      .then(res => setRows(res.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [upload.id, token]);

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-green-500" /></div>;
  if (rows.length === 0) return <a href={fileUrl} download={upload.filename} className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"><Download className="w-4 h-4" /> Download</a>;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 mt-2">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i === 0 ? "bg-gray-100 font-semibold" : i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {row.map((cell, j) => <td key={j} className="px-3 py-2 border-b border-gray-200">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
